/* eslint-disable fp/no-loops */
import type { SingleCommands } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { cellAround, TableMap } from '@tiptap/pm/tables';
import lodash from 'lodash';

export function updateHandle(view: $TSFixMe, value: $TSFixMe, key: $TSFixMe) {
  view.dispatch(view.state.tr.setMeta(key, { setHandle: value }));
}

export function resetHandle(view: $TSFixMe, key: $TSFixMe) {
  const pluginState = key.getState(view.state);
  if (pluginState.activeHandle > -1 && !pluginState.dragging) updateHandle(view, -1, key);
}

export function domCellAround(target: $TSFixMe) {
  // eslint-disable-next-line fp/no-loops
  while (target && target.nodeName !== 'TD' && target.nodeName !== 'TH')
    // eslint-disable-next-line no-param-reassign
    target = target.classList.contains('ProseMirror') ? null : target.parentNode;
  return target;
}

export function edgeCell(view: $TSFixMe, event: $TSFixMe, side: $TSFixMe, isVertical = false) {
  const found = view.posAtCoords({ left: event.clientX, top: event.clientY });
  if (!found) return -1;
  const { inside } = found;
  const $cell = cellAround(view.state.doc.resolve(inside + 1));
  if (!$cell) return -1;
  const direction = isVertical ? 'bottom' : 'right';
  if (side === direction) return $cell.pos;
  const map = TableMap.get($cell.node(-1));
  const start = $cell.start(-1);
  const index = map.map.indexOf($cell.pos - start);
  return index % map.width === 0 ? -1 : start + map.map[index - 1];
}

export function updateColumns({
  node,
  colgroup,
  table,
  cellMinWidth,
  commands,
  overrideCol,
  overrideValue,
  shouldUpdateState,
}: {
  node: ProseMirrorNode;
  colgroup: Element;
  table: HTMLElement;
  cellMinWidth: number;
  commands: SingleCommands;
  overrideCol?: number;
  overrideValue?: number;
  shouldUpdateState: boolean;
}) {
  let totalWidth = 0,
    fixedWidth = true;
  let nextDOM = colgroup.firstChild;
  const row = node.firstChild;

  if (row === null) {
    return;
  }

  for (let i = 0, col = 0; i < row.childCount; i++) {
    const { colspan, colwidth } = row.child(i).attrs;
    for (let j = 0; j < colspan; j++, col++) {
      const hasWidth = overrideCol === col ? overrideValue : colwidth && colwidth[j];
      const cssWidth = hasWidth ? hasWidth + 'px' : '';
      totalWidth += hasWidth || cellMinWidth;
      if (!hasWidth) fixedWidth = false;
      if (!nextDOM) {
        const col = document.createElement('col');
        col.style.width = cssWidth;
        colgroup.appendChild(col);
      } else {
        // @ts-expect-error
        if (nextDOM.style.width !== cssWidth) {
          // @ts-expect-error
          nextDOM.style.width = cssWidth;
        }
        nextDOM = nextDOM.nextSibling;
      }
    }
  }

  while (nextDOM) {
    const after = nextDOM.nextSibling;
    if (nextDOM.parentNode === null) {
      return;
    }
    nextDOM.parentNode.removeChild(nextDOM);
    nextDOM = after;
  }

  if (shouldUpdateState) {
    // If we'll start updating state while dragging the live feedback will be lost, because state changes break that.
    updateColsWidthAttrs(colgroup, node, commands);
  }

  if (fixedWidth) {
    table.style.width = totalWidth + 'px';
    table.style.minWidth = '';
  } else {
    table.style.width = '';
    table.style.minWidth = totalWidth + 'px';
  }
}

function updateColsWidthAttrs(colgroup: Element, table: ProseMirrorNode, commands: SingleCommands) {
  const colsWidth = Array.from(colgroup.children).map(col => (col as HTMLElement).offsetWidth);
  const colsMinWidth = colsWidth.map(width => Math.min(width, 120));

  if (
    !lodash.isEqual(table.attrs.dimensions?.colsWidthRatio, colsWidth) ||
    !lodash.isEqual(table.attrs.dimensions?.colsMinWidth, colsMinWidth)
  ) {
    try {
      commands.updateNodeAttrsById(table.attrs.id, {
        ...table.attrs,
        dimensions: {
          ...table.attrs.dimensions,
          colsWidthRatio: colsWidth,
          colsMinWidth,
        },
      });
    } catch (e) {
      console.error('could not update node attributes by id', e);
    }
  }
}
